import {
  MilestoneErrorModal,
  MilestoneStatusConfirmModal,
} from "@/containers/Project/components";
import {
  getMilestoneApproversAndNotificationUserIds,
  hasRequiredApprovers,
  projectHasRejectedMilestone,
} from "@/containers/Project/helper";
import {
  ProjectMilestoneStatus,
  ProjectMilestoneStatusActions,
  ProjectUserPermissions,
} from "@/enums";
import { projectMilestoneType } from "@/propTypes";
import { useProjectContext } from "@/providers";
import {
  useGetDocumentsForMilestoneQuery,
  useHasInactiveUsersMutation,
} from "@/services";
import { Button, Col, Row, Typography } from "antd";
import _ from "lodash";
import { bool } from "prop-types";
import { useState } from "react";
import { useIntl } from "react-intl";

export const MilestonePanelRelease = ({ milestone, skuRequired }) => {
  const intl = useIntl();
  const {
    canUser,
    canProjectBeCompleted,
    milestoneStatusChange,
    isProjectComplete,
    setMilestoneStatusActionCommentModal,
    project,
  } = useProjectContext();

  const [errorModal, setErrorModal] = useState({
    visible: false,
    title: null,
    description: null,
    errors: [],
  });
  const [confirmCompleteModal, setConfirmCompleteModal] = useState({
    visible: false,
    action: null,
  });

  const { data: projectDocuments } = useGetDocumentsForMilestoneQuery({
    projectId: project.id,
    milestoneId: milestone.id,
  });

  const [getInactiveUsers] = useHasInactiveUsersMutation();

  const hasRequiredDoc = () =>
    !milestone?.documentsRequired || projectDocuments[milestone.id]?.length;

  const passesSkuRequirements = () =>
    !skuRequired || project?.stockKeepingUnits?.length > 0;

  const fulfillsMilestoneAttributeTargetDateRequired = () => {
    if (!milestone.targetDatesRequired) {
      return true;
    }

    for (const schedule of project.schedules.filter(
      (s) =>
        !s.isDeleted &&
        s.milestones &&
        s.milestones.length > 0 &&
        s.milestones.some((m) => m.id === milestone.id)
    )) {
      // If none are found, treat the whole list of milestones as if there is a targetDatesRequired milestone at the front.
      const targetDatesRequiredMilestoneIndex =
        schedule.milestones.findIndex(
          (scheduleMilestone) => scheduleMilestone.targetDatesRequired
        ) || 0;

      if (
        targetDatesRequiredMilestoneIndex >= 0 &&
        targetDatesRequiredMilestoneIndex < schedule.milestones.length - 1
      ) {
        const subsequentMilestones = schedule.milestones.slice(
          targetDatesRequiredMilestoneIndex,
          schedule.milestones.length
        );

        if (
          subsequentMilestones.some(
            (subsequentMilestone) =>
              isMilestoneEditableStatus(subsequentMilestone.status) &&
              !subsequentMilestone.targetDate &&
              !subsequentMilestone.isDeleted
          )
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const isMilestoneEditableStatus = (status) =>
    status === ProjectMilestoneStatus.Draft ||
    status === ProjectMilestoneStatus.Rejected;

  const hasTargetDate = () => !!milestone.targetDate;

  const hasActiveRequiredApprovers = async () => {
    const users = getMilestoneApproversAndNotificationUserIds(
      project,
      milestone.approvers,
      milestone.projectLeaderApprovers
    );
    const hasInactiveUsers = await getInactiveUsers(users).unwrap();
    return !hasInactiveUsers;
  };

  const hasActiveNotificationUsersRequired = async () => {
    const users = getMilestoneApproversAndNotificationUserIds(
      project,
      milestone.notification?.roles,
      milestone.projectLeaderNotifications
    );
    const hasInactiveUsers = await getInactiveUsers(users).unwrap();
    return !hasInactiveUsers;
  };

  const hasRequiredNotificationUsers = () =>
    !milestone.notification?.roles?.some(
      (notificationRole) =>
        !notificationRole.isNotApplicable &&
        notificationRole.regionId &&
        (notificationRole.assignedUsers?.length || 0) === 0
    ) ?? true;

  const handleCloseModal = () => {
    setErrorModal({
      visible: false,
      title: null,
      description: null,
      errors: [],
    });
  };

  const handleConfirmOk = () => {
    milestoneStatusChange(
      project.id,
      milestone.id,
      confirmCompleteModal.action
    );
    setConfirmCompleteModal({ visible: false, action: null });
  };

  const handleConfirmCancel = () => {
    setConfirmCompleteModal({ visible: false, action: null });
  };

  const submitStatusActionWithValidation = async (action) => {
    let requiredByAction = {
      actualDate: !!milestone.actualDate,
      skus: passesSkuRequirements(),
      targetDatesRequired: fulfillsMilestoneAttributeTargetDateRequired(),
      targetDateRequired: hasTargetDate(),
      approversRequired: hasRequiredApprovers(
        milestone,
        project?.projectLeaders
      ),
      notificationUsersRequired: hasRequiredNotificationUsers(),
    };

    if (milestone.shouldCompleteProject) {
      requiredByAction = {
        ...requiredByAction,
        projectComplete: !!canProjectBeCompleted(project),
      };
    }

    if (action === ProjectMilestoneStatusActions.SubmitForApproval) {
      requiredByAction = {
        ...requiredByAction,
        document: hasRequiredDoc(),
        activeApproversRequired: await hasActiveRequiredApprovers(),
        activeNotificationUsersRequiredForApproval:
          await hasActiveNotificationUsersRequired(),
      };
    }

    if (action === ProjectMilestoneStatusActions.Release) {
      requiredByAction = {
        ...requiredByAction,
        document: hasRequiredDoc(),
        activeNotificationUsersRequiredForRelease:
          await hasActiveNotificationUsersRequired(),
      };
    }

    const errors = Object.keys(requiredByAction)
      .filter((validation) => !requiredByAction[validation])
      ?.map((error) =>
        intl.formatMessage(
          { id: `milestonesList.milestone${_.upperFirst(error)}Error` },
          { milestoneName: milestone.name }
        )
      );

    if (errors.length) {
      setErrorModal({
        visible: true,
        title: intl.formatMessage(
          { id: `milestonesList.milestone${_.upperFirst(action)}Title` },
          { milestoneName: milestone.name }
        ),
        description: intl.formatMessage({
          id: `milestonesList.milestone${_.upperFirst(action)}Description`,
        }),
        errors: errors,
      });
      return;
    }

    if (milestone.shouldCompleteProject) {
      if (projectHasRejectedMilestone(project)) {
        setMilestoneStatusActionCommentModal({
          milestoneId: milestone.id,
          targetStatus:
            action === ProjectMilestoneStatusActions.SubmitForApproval
              ? ProjectMilestoneStatus.InApproval
              : ProjectMilestoneStatus.Released,
          finalApprover: false,
        });
        return;
      } else if (
        action === ProjectMilestoneStatusActions.Release ||
        action === ProjectMilestoneStatusActions.SubmitForApproval
      ) {
        setConfirmCompleteModal({ visible: true, action: action });
        return;
      }
    }

    milestoneStatusChange(project.id, milestone.id, action);
  };

  const editMilestoneState =
    milestone.status === ProjectMilestoneStatus.Draft ||
    milestone.status === ProjectMilestoneStatus.Rejected;

  const isSubmitForApproval =
    milestone.approvalRequired &&
    editMilestoneState &&
    canUser(ProjectUserPermissions.SubmitAndReleaseMilestones);

  const isRelease =
    !milestone.approvalRequired &&
    milestone.status !== ProjectMilestoneStatus.Released &&
    canUser(ProjectUserPermissions.SubmitAndReleaseMilestones);

  if (!isSubmitForApproval && !isRelease) return null;

  const label = isSubmitForApproval ? "submitForApproval" : "release";
  const actionType = isSubmitForApproval
    ? ProjectMilestoneStatusActions.SubmitForApproval
    : ProjectMilestoneStatusActions.Release;

  return (
    <Row>
      <Col className="pb-2">
        {!isProjectComplete && (
          <>
            <Typography.Title level={4}>
              {intl.formatMessage({
                id: `milestonesList.${label}`,
              })}
            </Typography.Title>
            <Button
              data-test-id="milestone-action-btn"
              onClick={async () =>
                await submitStatusActionWithValidation(actionType)
              }
              type="primary"
              disabled={
                !canUser(ProjectUserPermissions.SubmitAndReleaseMilestones)
              }
            >
              {intl.formatMessage({ id: `milestonesList.${label}` })}
            </Button>
          </>
        )}
        <MilestoneErrorModal
          modalState={errorModal}
          closeModal={handleCloseModal}
        />
        <MilestoneStatusConfirmModal
          modalState={confirmCompleteModal}
          handleOk={handleConfirmOk}
          handleCancel={handleConfirmCancel}
        />
      </Col>
    </Row>
  );
};

MilestonePanelRelease.propTypes = {
  milestone: projectMilestoneType.isRequired,
  skuRequired: bool,
};
